// src/state/stateRepo.js
// ETag per projectId (niet globaal)
const etagByProject = new Map();

export function assertProjectId(projectId) {
  if (!/^[a-z0-9]+([-_][a-z0-9]+)*$/.test(String(projectId || ""))) {
    throw new Error("Invalid projectId (expected lowercase slug, e.g. giga_ea).");
  }
}

export function defaultState() {
  return {
    schemaVersion: 1,
    contentVersion: 1,
    ui: { activeTab: "fase1" }, // "fase1" | "fase2" | "radar"
    data: {
      checkedActions: {}, // actionId -> boolean
      updates: {},        // sleutel: "{themeId}_{phase}" -> array van {timestamp, email, text}
    },
  };
}

export function defaultStatePL() {
  return {
    schemaVersion: 1,
    contentVersion: 1,
    ui: {
      activeDomain: null,       // wordt gevuld met eerste domein uit project.Domains
      activeSprint: "overview", // "overview" | "sprint1" | ... | "sprint6"
    },
    data: {
      checkedActions: {}, // sleutel: "{domain}_{sprintId}_{deliverableId}_{actieId}" -> boolean
      updates: {},        // sleutel: "{domain}_{sprintId}_{deliverableId}" -> array van {timestamp, email, text}
    },
  };
}

export function normalizeStatePL(s) {
  const d = defaultStatePL();
  return {
    ...d,
    ...(s || {}),
    ui: { ...d.ui, ...(s?.ui || {}) },
    data: {
      checkedActions: { ...d.data.checkedActions, ...(s?.data?.checkedActions || {}) },
      updates: { ...d.data.updates, ...(s?.data?.updates || {}) },
    },
    schemaVersion: Number.isFinite(Number(s?.schemaVersion)) ? Number(s.schemaVersion) : 1,
    contentVersion: Number.isFinite(Number(s?.contentVersion)) ? Number(s.contentVersion) : 1,
  };
}

export function normalizeState(s) {
  const d = defaultState();
  const merged = {
    ...d,
    ...(s || {}),
    ui: { ...d.ui, ...(s?.ui || {}) },
    data: {
      checkedActions: { ...d.data.checkedActions, ...(s?.data?.checkedActions || {}) },
      updates: { ...d.data.updates, ...(s?.data?.updates || {}) },
    },
  };

  // Force numeric versions (API expects numbers)
  merged.schemaVersion = Number.isFinite(Number(merged.schemaVersion))
    ? Number(merged.schemaVersion)
    : 1;

  merged.contentVersion = Number.isFinite(Number(merged.contentVersion))
    ? Number(merged.contentVersion)
    : 1;

  return merged;
}

async function apiGetState(projectId, { ifNoneMatch, normalize = normalizeState } = {}) {
  assertProjectId(projectId);

  const headers = {};
  if (ifNoneMatch) headers["If-None-Match"] = ifNoneMatch;

  const res = await fetch(`/api/state?projectId=${encodeURIComponent(projectId)}`, {
    method: "GET",
    headers,
  });

  if (res.status === 404) return { found: false, state: null, etag: null };
  if (res.status === 401) throw new Error("Not authenticated (401).");
  if (res.status === 403) throw new Error("Forbidden (403).");
  if (res.status === 304) {
    return { found: true, notModified: true, state: null, etag: res.headers.get("ETag") };
  }
  if (!res.ok) throw new Error(`GET /api/state failed (${res.status}).`);

  const etag = res.headers.get("ETag");
  const body = await res.json();

  // jouw API geeft stateJson als string terug
  const parsed = normalize(JSON.parse(body.stateJson));
  return { found: true, state: parsed, etag };
}

async function apiPostState(projectId, state, etag) {
  assertProjectId(projectId);

  const headers = { "content-type": "application/json" };
  if (etag) headers["If-Match"] = etag;

  const payload = {
    stateJson: JSON.stringify(state),
    schemaVersion: state.schemaVersion,
    contentVersion: state.contentVersion,
  };

  const res = await fetch(`/api/state?projectId=${encodeURIComponent(projectId)}`, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });

  if (res.status === 401) throw new Error("Not authenticated (401).");
  if (res.status === 403) throw new Error("Forbidden (403).");

  if (res.status === 412) {
    const err = new Error("ETag mismatch (412).");
    err.code = 412;
    throw err;
  }

  if (res.status === 428) {
    const err = new Error("Missing If-Match (428).");
    err.code = 428;
    throw err;
  }

  if (!res.ok) throw new Error(`POST /api/state failed (${res.status}).`);

  return res.headers.get("ETag") || null;
}

/**
 * Public: loadState (async)
 * - Tries server first
 * - If no server record, migrates localStorage -> server (one-time), then clears localStorage
 */
export async function loadState(projectId) {
  assertProjectId(projectId);

  // 1) server first
  const server = await apiGetState(projectId);
  if (server.found) {
    etagByProject.set(projectId, server.etag);
    return server.state;
  }

  return defaultState();
}

export async function refreshState(projectId) {
  assertProjectId(projectId);

  const etag = etagByProject.get(projectId) ?? null;
  const r = await apiGetState(projectId, { ifNoneMatch: etag });

  if (r.notModified) return { updated: false };

  if (r.found && r.state) {
    etagByProject.set(projectId, r.etag);
    return { updated: true, state: r.state };
  }

  return { updated: false };
}

/**
 * Public: saveState (assessment)
 * - Direct POST with If-Match
 * - On 412: refetches latest server state, updates currentEtag, and calls onConflict(freshState)
 */
export async function saveState(projectId, state, { onConflict, onError } = {}) {
  assertProjectId(projectId);

  try {
    const currentEtag = etagByProject.get(projectId) ?? null;
    const newEtag = await apiPostState(projectId, normalizeState(state), currentEtag);
    etagByProject.set(projectId, newEtag);
  } catch (e) {
    if (e?.code === 412) {
      try {
        const fresh = await apiGetState(projectId);
        if (fresh.found) {
          etagByProject.set(projectId, fresh.etag);
          if (typeof onConflict === "function") onConflict(fresh.state);
        }
      } catch (inner) {
        if (typeof onError === "function") onError(inner);
      }
    } else {
      if (typeof onError === "function") onError(e);
    }
  }
}

// ─── Projectleiders-specifieke varianten ─────────────────────────────────────

export async function loadStatePL(projectId) {
  assertProjectId(projectId);

  const server = await apiGetState(projectId, { normalize: normalizeStatePL });
  if (server.found) {
    etagByProject.set(projectId, server.etag);
    return server.state;
  }

  return defaultStatePL();
}

export async function refreshStatePL(projectId) {
  assertProjectId(projectId);

  const etag = etagByProject.get(projectId) ?? null;
  const r = await apiGetState(projectId, { ifNoneMatch: etag, normalize: normalizeStatePL });

  if (r.notModified) return { updated: false };

  if (r.found && r.state) {
    etagByProject.set(projectId, r.etag);
    return { updated: true, state: r.state };
  }

  return { updated: false };
}

export async function saveStatePL(projectId, state, { onConflict, onError } = {}) {
  assertProjectId(projectId);

  try {
    const currentEtag = etagByProject.get(projectId) ?? null;
    const newEtag = await apiPostState(projectId, normalizeStatePL(state), currentEtag);
    etagByProject.set(projectId, newEtag);
  } catch (e) {
    if (e?.code === 412) {
      try {
        const fresh = await apiGetState(projectId, { normalize: normalizeStatePL });
        if (fresh.found) {
          etagByProject.set(projectId, fresh.etag);
          if (typeof onConflict === "function") onConflict(fresh.state);
        }
      } catch (inner) {
        if (typeof onError === "function") onError(inner);
      }
    } else {
      if (typeof onError === "function") onError(e);
    }
  }
}

// ─── Volwassenheid-specifieke varianten ──────────────────────────────────────

export function defaultStateVW() {
  return {
    schemaVersion: 1,
    contentVersion: 1,
    ui: {
      activeDomain: null,        // wordt gevuld met eerste domein uit project.Domains
      activePijler: "overview",  // "overview" | pijlerId
      activeNiveau: "niveau-1",  // "niveau-1" | "niveau-2" | … | "niveau-5"
    },
    data: {
      checkedActions: {}, // sleutel: "{san(domain)}_{san(deliverableId)}_{san(actieId)}"
      updates: {},        // sleutel: "{san(domain)}_{san(deliverableId)}"
    },
  };
}

export function normalizeStateVW(s) {
  const d = defaultStateVW();
  return {
    ...d,
    ...(s || {}),
    ui: { ...d.ui, ...(s?.ui || {}) },
    data: {
      checkedActions: { ...d.data.checkedActions, ...(s?.data?.checkedActions || {}) },
      updates: { ...d.data.updates, ...(s?.data?.updates || {}) },
    },
    schemaVersion: Number.isFinite(Number(s?.schemaVersion)) ? Number(s.schemaVersion) : 1,
    contentVersion: Number.isFinite(Number(s?.contentVersion)) ? Number(s.contentVersion) : 1,
  };
}

export async function loadStateVW(projectId) {
  assertProjectId(projectId);

  const server = await apiGetState(projectId, { normalize: normalizeStateVW });
  if (server.found) {
    etagByProject.set(projectId, server.etag);
    return server.state;
  }

  return defaultStateVW();
}

export async function refreshStateVW(projectId) {
  assertProjectId(projectId);

  const etag = etagByProject.get(projectId) ?? null;
  const r = await apiGetState(projectId, { ifNoneMatch: etag, normalize: normalizeStateVW });

  if (r.notModified) return { updated: false };

  if (r.found && r.state) {
    etagByProject.set(projectId, r.etag);
    return { updated: true, state: r.state };
  }

  return { updated: false };
}

export async function saveStateVW(projectId, state, { onConflict, onError } = {}) {
  assertProjectId(projectId);

  try {
    const currentEtag = etagByProject.get(projectId) ?? null;
    const newEtag = await apiPostState(projectId, normalizeStateVW(state), currentEtag);
    etagByProject.set(projectId, newEtag);
  } catch (e) {
    if (e?.code === 412) {
      try {
        const fresh = await apiGetState(projectId, { normalize: normalizeStateVW });
        if (fresh.found) {
          etagByProject.set(projectId, fresh.etag);
          if (typeof onConflict === "function") onConflict(fresh.state);
        }
      } catch (inner) {
        if (typeof onError === "function") onError(inner);
      }
    } else {
      if (typeof onError === "function") onError(e);
    }
  }
}